/* global console */
import * as React from "react";
import { ArrowLeft, Loader2 } from "lucide-react";

import {
  CITATION_REFS_TAG_KEY,
  CitationDrawer,
  groupItemsByDataSource,
  readPool,
  type Citation,
  type CitationGroup,
} from "../citations";
import { getSlideTagsViaIntents } from "../services/factset/intentBridgeService";
import { shouldUseFactSetBridge } from "../services/presentationHost";
import { CitationCard } from "./CitationCard";

interface SlideCitationsPanelProps {
  /** 0-based slide index (matches `Tab.session.slideIndex`). */
  slideIndex: number;
  /** Displayed PowerPoint slide number for labels. */
  slideNumber?: number;
  /** Slide id (resolved by tab session, possibly empty if not yet known). */
  slideId?: string;
  /** Called when the user taps the back chevron. */
  onClose: () => void;
}

interface ResolvedRef {
  id: string;
  shapeId: string;
  citation: Citation;
}

interface LoadState {
  status: "idle" | "loading" | "ready" | "error";
  message?: string;
}

/**
 * Read the slide's `finster:citationRefs` tag + the deck-wide
 * `finster:citationPool`, resolve refs to `Citation` records, and
 * render them grouped by data source (matches Figma frame `12` -
 * node 2645:18621).
 *
 * Failure modes:
 *
 *   - Host doesn't surface tags on this slide -> renders an empty
 *     state with a brief explanation.
 *   - Tag JSON is malformed -> swallowed, treated as empty.
 *   - Pool fetch failure -> renders an error block but keeps the
 *     back navigation intact.
 *
 * Recomputes whenever `slideIndex` or `slideId` changes.
 */
export const SlideCitationsPanel: React.FC<SlideCitationsPanelProps> = ({
  slideIndex,
  slideNumber,
  slideId,
  onClose,
}) => {
  const [state, setState] = React.useState<LoadState>({ status: "idle" });
  const [resolved, setResolved] = React.useState<ResolvedRef[]>([]);
  const [pool, setPool] = React.useState<Citation[]>([]);
  const [drawer, setDrawer] = React.useState<{ open: boolean; citationId: string | null }>({
    open: false,
    citationId: null,
  });

  React.useEffect(() => {
    let cancelled = false;

    const load = async (): Promise<void> => {
      setState({ status: "loading" });
      try {
        const [tags, envelope] = await Promise.all([
          getSlideTagsViaIntents({ slideId: slideId || undefined, index: slideIndex }),
          readPool(),
        ]);

        if (cancelled) return;

        const refMap = parseRefsTag(tags?.[CITATION_REFS_TAG_KEY]);
        const allCitations = Object.values(envelope.citations);
        setPool(allCitations);

        const resolvedList: ResolvedRef[] = [];
        const seenIds = new Set<string>();
        for (const [shapeId, ids] of Object.entries(refMap)) {
          for (const id of ids) {
            if (seenIds.has(id)) continue;
            const citation = envelope.citations[id];
            if (!citation) continue;
            seenIds.add(id);
            resolvedList.push({ id, shapeId, citation });
          }
        }

        setResolved(resolvedList);
        setState({ status: "ready" });
      } catch (error) {
        if (cancelled) return;
        const message = error instanceof Error ? error.message : String(error);
        console.warn("[SlideCitationsPanel] load failed:", message);
        setState({ status: "error", message });
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [slideIndex, slideId]);

  const groups = React.useMemo(() => groupByDataSource(resolved), [resolved]);

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <div className="flex items-center gap-2 border-b border-border bg-background px-4 py-2">
        <button
          type="button"
          onClick={onClose}
          aria-label="Back"
          className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-foreground hover:bg-muted"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <h2 className="truncate text-[14px] font-medium text-foreground">
          Slide {slideNumber ?? slideIndex + 1} • Citations
        </h2>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {state.status === "loading" && (
          <div className="flex h-full items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            <span className="text-[12px]">Loading citations…</span>
          </div>
        )}

        {state.status === "error" && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-[12px] text-destructive">
            Couldn&apos;t load citations for this slide.
            {state.message ? <div className="mt-1 opacity-80">{state.message}</div> : null}
          </div>
        )}

        {state.status === "ready" && resolved.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-1 px-4 py-8 text-center">
            <p className="text-[13px] font-medium text-foreground">No citations on this slide</p>
            <p className="text-[12px] text-muted-foreground">
              When the assistant cites data from {shouldUseFactSetBridge() ? "FactSet AI" : "Finster"}, the sources will appear here.
            </p>
          </div>
        )}

        {state.status === "ready" && resolved.length > 0 && (
          <div className="flex flex-col gap-4">
            {groups.map((group) => (
              <section key={group.label} className="flex flex-col gap-2">
                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {group.label}
                </h3>
                <div className="flex flex-col gap-2">
                  {group.items.map((item) => (
                    <CitationCard
                      key={`${item.shapeId}:${item.id}`}
                      citationId={item.id}
                      citation={item.citation}
                      citations={pool}
                      onView={(id) => setDrawer({ open: true, citationId: id })}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>

      <CitationDrawer
        open={drawer.open}
        onOpenChange={(open) => setDrawer((prev) => ({ ...prev, open }))}
        citationId={drawer.citationId}
        citations={pool}
      />
    </div>
  );
};

function parseRefsTag(raw: string | undefined): Record<string, string[]> {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    const out: Record<string, string[]> = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (Array.isArray(value)) {
        out[key] = value.filter((v): v is string => typeof v === "string");
      }
    }
    return out;
  } catch {
    return {};
  }
}

function groupByDataSource(refs: ResolvedRef[]): CitationGroup<ResolvedRef>[] {
  return groupItemsByDataSource(refs);
}
